import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from 'react-native'
import { X } from 'lucide-react-native'
import { useSupabaseAuth } from '../../../shared/contexts/SupabaseAuthContext'
import { useZportiAdminAccess } from '../../hooks/useZportiAdminAccess'
import { useImpersonation } from '../../hooks/useImpersonation'
import { WEBSITE_UNIMPERSONATE_PATH } from '../../constants/impersonationRoute.constants'
import {
  isImpersonationTargetDraftValid,
  isImpersonationTargetUserIdFormat,
} from '../../helpers/impersonationTarget.helper'
import { useTheme } from '../../../theme/useTheme'
import { getOperatorWebPaletteStyles } from './OperatorWebPalette.styles'

export interface OperatorWebPalettePanelProps {
  onClose: () => void
}

/**
 * Operator read-only impersonation form. Rendered inside {@link OperatorWebPaletteHost}
 * so the browser URL stays on the current marketing page (`/`, `/privacy`, …).
 */
export function OperatorWebPalettePanel({ onClose }: OperatorWebPalettePanelProps) {
  const theme = useTheme()
  const styles = useMemo(() => getOperatorWebPaletteStyles(theme), [theme])
  const { isLoaded, isSignedIn } = useSupabaseAuth()
  const { isAdmin, loading } = useZportiAdminAccess()
  const { startImpersonation, busy: impersonationBusy } = useImpersonation()
  const [draft, setDraft] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)

  const dismiss = useCallback(() => {
    onClose()
  }, [onClose])

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        dismiss()
      }
    }
    document.addEventListener('keydown', onEsc, true)
    return () => document.removeEventListener('keydown', onEsc, true)
  }, [dismiss])

  useEffect(() => {
    if (!isLoaded || loading) return
    if (!isSignedIn || !isAdmin) {
      onClose()
    }
  }, [isAdmin, isLoaded, isSignedIn, loading, onClose])

  const onContinue = useCallback(async () => {
    const t = draft.trim()
    if (!t) {
      setFieldError('Enter an email or user id.')
      return
    }
    if (!isImpersonationTargetDraftValid(t)) {
      setFieldError('Use a valid email or UUID-shaped user id.')
      return
    }
    setFieldError(null)
    const payload = isImpersonationTargetUserIdFormat(t)
      ? { targetUserId: t }
      : { targetEmail: t }
    const ok = await startImpersonation(payload)
    if (!ok) {
      setFieldError('Could not start impersonation. Check the target and try again.')
      return
    }
    onClose()
  }, [draft, onClose, startImpersonation])

  if (Platform.OS !== 'web') {
    return null
  }

  if (!isLoaded || loading || !isSignedIn || !isAdmin) {
    return (
      <View style={styles.root}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Operator</Text>
          <Pressable
            onPress={dismiss}
            style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.75 }]}
            accessibilityRole="button"
            accessibilityLabel="Close"
          >
            <X size={22} color={theme.black} />
          </Pressable>
        </View>
        <View style={{ padding: theme.spacing.xxl, alignItems: 'center' }}>
          <ActivityIndicator size="large" color={theme.primary} />
        </View>
      </View>
    )
  }

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Operator</Text>
        <Pressable
          onPress={dismiss}
          style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.75 }]}
          accessibilityRole="button"
          accessibilityLabel="Close"
        >
          <X size={22} color={theme.black} />
        </Pressable>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <Text style={styles.lead}>
          This panel sits on top of the page you already have open — the address bar does not change when you tap
          Continue.
          {'\n\n'}
          From `/` or other marketing routes, press Cmd+K (Mac) or Ctrl+K (Windows) to open or close this panel. When you
          are done viewing as someone else on the site, open {WEBSITE_UNIMPERSONATE_PATH} or use the strip on `/`.
        </Text>
        <TextInput
          style={styles.input}
          value={draft}
          onChangeText={(v) => {
            setDraft(v)
            if (fieldError) setFieldError(null)
          }}
          placeholder="Email or user id"
          placeholderTextColor={theme.gray[500]}
          autoCapitalize="none"
          autoCorrect={false}
          editable={!impersonationBusy}
          testID="operator-web-palette-input"
        />
        <Text style={styles.hint}>
          Continue starts read-only impersonation without navigating away. You can also use a bookmark to{' '}
          {`/site-impersonate?email=…`} (same outcome, still returns to `/`).
        </Text>
        {fieldError ? <Text style={styles.fieldError}>{fieldError}</Text> : null}
        <View style={styles.actions}>
          <Pressable
            onPress={() => void onContinue()}
            disabled={impersonationBusy}
            style={({ pressed }) => [
              styles.btnPrimary,
              (pressed || impersonationBusy) && { opacity: impersonationBusy ? 0.6 : 0.9 },
            ]}
            accessibilityRole="button"
            accessibilityLabel="Continue impersonation"
            testID="operator-web-palette-continue"
          >
            <Text style={styles.btnPrimaryText}>
              {impersonationBusy ? 'Starting…' : 'Continue'}
            </Text>
          </Pressable>
          <Pressable
            onPress={dismiss}
            disabled={impersonationBusy}
            style={({ pressed }) => [styles.btnSecondary, pressed && { opacity: 0.85 }]}
            accessibilityRole="button"
            accessibilityLabel="Cancel"
          >
            <Text style={styles.btnSecondaryText}>Cancel</Text>
          </Pressable>
        </View>
      </ScrollView>
    </View>
  )
}
